import { Invoice, ServiceRequest } from '@medplum/fhirtypes';
import { TFunction } from 'i18next';
import { IconSearch } from '@tabler/icons-react';
import {
  SearchFieldDefinition,
  ResourceSearchConfig,
  FhirSearchParams,
} from '@/components/resource-search/ResourceSearchForm.types';
import { ResourceTableColumn } from '@/components/resource-search/ResourceTable';
import { PaymentStatusBadge } from '../components/PaymentStatusBadge';
import { PaymentStatus } from '../lims-types';
import { getLinkedOrderRef } from '../utils/payment-helpers';
import { OrderUtils } from '../utils/order-table.utils';
import { getOrderIdSuggestions } from '../utils/fhir-helpers';
import { EXTERNAL_CS, SYSTEMS } from '@consts/systems.consts';

// -----------------------------------------------------------------------------
export interface PaymentSearchParams {
  patientName?: string;
  status?: string;
  dateFrom?: Date | null;
  dateTo?: Date | null;
  orderId?: string;
}

// -----------------------------------------------------------------------------
export const PAYMENT_SEARCH_DEFAULTS: PaymentSearchParams = {
  patientName: undefined,
  status: undefined,
  dateFrom: null,
  dateTo: null,
  orderId: undefined,
};

// -----------------------------------------------------------------------------
// FHIR Invoice statuses
const INVOICE_STATUSES = [
  'draft',
  'issued',
  'balanced',
  'cancelled',
  'entered-in-error',
] as const;

// -----------------------------------------------------------------------------
export const paymentSearchConfig: ResourceSearchConfig<
  PaymentSearchParams,
  Invoice
> = {
  resourceType: 'Invoice',
  buildSearchQuery: (params, page, itemsPerPage) => {
    // When filtering by orderId we do client-side filtering after fetching,
    // so boost the page size to avoid pagination hiding matches.
    const effectiveCount = params.orderId ? 500 : itemsPerPage;
    const effectiveOffset = params.orderId ? 0 : (page - 1) * itemsPerPage;

    const query: FhirSearchParams = {
      _count: effectiveCount,
      _offset: effectiveOffset,
      _sort: '-date',
    };

    if (params.status) {
      query.status = params.status;
    }

    if (params.patientName) {
      query['subject:Patient.name'] = params.patientName;
    }

    if (params.dateFrom && params.dateTo) {
      const from = params.dateFrom.toISOString().split('T')[0];
      const to = params.dateTo.toISOString().split('T')[0];
      query._filter = `(date ge ${from} and date le ${to})`;
    } else if (params.dateFrom) {
      query.date = `ge${params.dateFrom.toISOString().split('T')[0]}`;
    } else if (params.dateTo) {
      query.date = `le${params.dateTo.toISOString().split('T')[0]}`;
    }

    return query;
  },

  needsClientSideFiltering: (params) => Boolean(params.orderId),

  clientSideFilter: async (invoices, params, medplum) => {
    if (!params.orderId) return invoices;
    const term = params.orderId.trim();
    // Find ServiceRequests whose identifier matches the searched term
    const matchedSRs = await medplum.searchResources('ServiceRequest', {
      category: `${SYSTEMS.SNOMED}|${EXTERNAL_CS.SNOMED.CODES.LABORATORY_PROCEDURE}`,
      identifier: term,
      _count: '100',
    });
    const matchedFhirIds = new Set(matchedSRs.map((sr) => sr.id).filter(Boolean) as string[]);
    return invoices.filter((inv) => {
      const ref = getLinkedOrderRef(inv);
      if (!ref) return false;
      const fhirId = ref.replace('ServiceRequest/', '');
      return matchedFhirIds.has(fhirId);
    });
  },

  getResourceIdSuggestions: getOrderIdSuggestions,
};

// -----------------------------------------------------------------------------
interface PaymentFieldOptions {
  t: TFunction<'lims'>;
  searchParams: PaymentSearchParams;
  onSearch: (updates: Partial<PaymentSearchParams>) => void;
  patientNameQuery: string;
  setPatientNameQuery: (value: string) => void;
  setPatientQuery: (value: string) => void;
  patientNameOptions: string[];
  patientLoading: boolean;
  patientNameLoading: boolean;
  orderIdQuery: string;
  setOrderIdQuery: (value: string) => void;
  orderIdOptions: string[];
  orderIdLoading: boolean;
}

// -----------------------------------------------------------------------------
export function getPaymentSearchFields({
  t,
  searchParams,
  onSearch,
  patientNameQuery,
  setPatientNameQuery,
  setPatientQuery,
  patientNameOptions,
  patientLoading,
  patientNameLoading,
  orderIdQuery,
  setOrderIdQuery,
  orderIdOptions,
  orderIdLoading,
}: PaymentFieldOptions): SearchFieldDefinition<PaymentSearchParams>[] {
  return [
    {
      key: 'patientName',
      type: 'autocomplete',
      placeholder: t('paymentsPage.searchPlaceholder', 'Search by patient name...', {
        ns: 'lims',
      }),
      icon: <IconSearch size={16} />,
      value: patientNameQuery,
      options: patientNameOptions,
      loading: patientLoading || patientNameLoading,
      onQueryChange: (value: string) => {
        setPatientQuery(value);
        setPatientNameQuery(value);
        if (!value) {
          onSearch({ patientName: undefined });
        }
      },
      onOptionSubmit: (value: string) => onSearch({ patientName: value }),
      onEnter: (value: string) => onSearch({ patientName: value }),
    },
    {
      key: 'orderId',
      type: 'autocomplete',
      placeholder: t('paymentsPage.orderIdPlaceholder', 'Search by order ID...', {
        ns: 'lims',
      }),
      icon: <IconSearch size={16} />,
      value: orderIdQuery,
      options: orderIdOptions,
      loading: orderIdLoading,
      onQueryChange: (value: string) => {
        setOrderIdQuery(value);
        if (!value) onSearch({ orderId: undefined });
      },
      onOptionSubmit: (value: string) => onSearch({ orderId: value || undefined }),
      onEnter: (value: string) => onSearch({ orderId: value || undefined }),
    },
    {
      key: 'status',
      type: 'select',
      placeholder: t('paymentsPage.statusPlaceholder', 'Select status...', {
        ns: 'lims',
      }),
      options: INVOICE_STATUSES.map((status) => ({
        value: status,
        label: t(`paymentsPage.statuses.${status}`, status, { ns: 'lims' }),
      })),
      value: searchParams.status,
      onChange: (value: string | null) =>
        onSearch({ status: value || undefined }),
    },
    {
      key: 'dateFrom',
      type: 'date',
      placeholder: t('paymentsPage.fromDatePlaceholder', 'From date', {
        ns: 'lims',
      }),
      value: searchParams.dateFrom,
      onChange: (value: Date | null) => onSearch({ dateFrom: value }),
    },
    {
      key: 'dateTo',
      type: 'date',
      placeholder: t('paymentsPage.toDatePlaceholder', 'To date', {
        ns: 'lims',
      }),
      value: searchParams.dateTo,
      onChange: (value: Date | null) => onSearch({ dateTo: value }),
    },
  ].filter(Boolean) as SearchFieldDefinition<PaymentSearchParams>[];
}

// -----------------------------------------------------------------------------
interface PaymentColumnOptions {
  t: TFunction<'lims'>;
  patients: Map<string, any>;
  serviceRequests: Map<string, ServiceRequest>;
}

// Utility function to format currency from Invoice amount (Money type)
function formatMoney(money?: { value?: number; currency?: string }): string {
  if (!money?.value && money?.value !== 0) return '-';
  return `${money.value.toFixed(2)} ${money.currency || '€'}`;
}

// -----------------------------------------------------------------------------
export function getPaymentTableColumns({
  t,
  patients,
  serviceRequests,
}: PaymentColumnOptions): ResourceTableColumn<Invoice>[] {
  return [
    {
      key: 'orderId',
      label: t('paymentsPage.table.orderId', 'Order ID', { ns: 'lims' }),
      render: (invoice) => {
        const ref = getLinkedOrderRef(invoice);
        if (!ref) return '-';
        const fhirId = ref.replace('ServiceRequest/', '');
        const sr = serviceRequests.get(fhirId);
        return sr ? OrderUtils.getOrderId(sr) : fhirId;
      },
    },
    {
      key: 'patient',
      label: t('paymentsPage.table.patient', 'Patient', { ns: 'lims' }),
      render: (invoice) => {
        const subjectRef = invoice.subject?.reference;
        if (subjectRef) {
          const patientId = subjectRef.replace('Patient/', '');
          const patient = patients.get(patientId);
          if (patient?.name?.[0]) {
            const name = patient.name[0];
            return `${name.family || ''} ${name.given?.join(' ') || ''}`.trim();
          }
        }
        return t('paymentsPage.unknown', 'Unknown', { ns: 'lims' });
      },
    },
    {
      key: 'totalGross',
      label: t('paymentsPage.table.totalGross', 'Gross Amount', { ns: 'lims' }),
      render: (invoice) => formatMoney(invoice.totalGross),
    },
    {
      key: 'discount',
      label: t('paymentsPage.table.discount', 'Discount', { ns: 'lims' }),
      render: (invoice) => {
        const gross = invoice.totalGross?.value || 0;
        const net = invoice.totalNet?.value || 0;
        const discountValue = Math.max(0, gross - net);
        return formatMoney({
          value: discountValue,
          currency: invoice.totalGross?.currency || '€',
        });
      },
    },
    {
      key: 'totalNet',
      label: t('paymentsPage.table.totalNet', 'Net Amount', { ns: 'lims' }),
      render: (invoice) => formatMoney(invoice.totalNet),
    },
    {
      key: 'status',
      label: t('paymentsPage.table.status', 'Status', { ns: 'lims' }),
      render: (invoice) => (
        <PaymentStatusBadge status={(invoice.status as PaymentStatus) || 'draft'} />
      ),
    },
    {
      key: 'date',
      label: t('paymentsPage.table.date', 'Date', { ns: 'lims' }),
      render: (invoice) => {
        const date = invoice.date || invoice.meta?.lastUpdated;
        return date ? new Date(date).toLocaleDateString() : '-';
      },
    },
  ];
}
